import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { LoadingSpinner } from '../loading-spinner-v2';
import { useAuth } from '@/contexts/auth-context';
import { cn } from '@/lib/utils';

const DEFAULT_SELECTION_REQUIRED_TITLES = ['Sửa', 'Xóa', 'Xoá', 'Sao chép'] as const;

type ActionBarSelectionContextValue = {
  hasSelection?: boolean;
  requiredTitles: readonly string[];
  message: string;
};

const ActionBarSelectionContext = createContext<ActionBarSelectionContextValue | null>(null);

type ActionBarLoadingContextValue = {
  beginLoading: () => () => void;
};

const ActionBarLoadingContext = createContext<ActionBarLoadingContextValue | null>(null);

export interface ActionBarV2Props {
  title?: string;
  titleComponent?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  inForm?: boolean;
  loading?: boolean;
  hasSelection?: boolean;
  selectionRequiredTitles?: readonly string[];
  selectionRequiredMessage?: string;
}

const normalizeActionTitle = (value?: string) => (value || '').trim().toLocaleLowerCase('vi-VN');

export const useActionBarSelectionRequirement = (title?: string) => {
  const context = useContext(ActionBarSelectionContext);
  const normalizedTitle = normalizeActionTitle(title);
  const requiresSelection =
    context?.requiredTitles.some(requiredTitle => normalizeActionTitle(requiredTitle) === normalizedTitle) || false;

  return {
    disabled: requiresSelection && context?.hasSelection === false,
    message: context?.message || 'Cần chọn dòng'
  };
};

const extractTextFromNode = (node: React.ReactNode): string => {
  if (!node) return '';
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractTextFromNode).filter(Boolean).join(' ').trim();
  }
  if (React.isValidElement(node)) {
    const props = node.props as any;
    if (props && props.children) {
      return extractTextFromNode(props.children);
    }
  }
  return '';
};

export const useActionBarLoading = () => useContext(ActionBarLoadingContext);

export function ActionBarV2({
  title,
  titleComponent,
  children,
  className,
  loading = false,
  hasSelection,
  selectionRequiredTitles = DEFAULT_SELECTION_REQUIRED_TITLES,
  selectionRequiredMessage = 'Cần chọn dòng'
}: ActionBarV2Props) {
  const { loading: authLoading } = useAuth();
  const [actionLoadingCount, setActionLoadingCount] = useState(0);

  React.useEffect(() => {
    let computedTitle = '';
    if (title) {
      computedTitle = title;
    } else if (titleComponent) {
      computedTitle = extractTextFromNode(titleComponent);
    }

    if (computedTitle) {
      const originalTitle = document.title;
      document.title = computedTitle;
      return () => {
        document.title = originalTitle;
      };
    }
  }, [title, titleComponent]);

  const beginLoading = useCallback(() => {
    let active = true;
    setActionLoadingCount(current => current + 1);

    return () => {
      if (!active) return;
      active = false;
      setActionLoadingCount(current => Math.max(0, current - 1));
    };
  }, []);

  const selectionContextValue = useMemo<ActionBarSelectionContextValue>(
    () => ({
      hasSelection,
      requiredTitles: selectionRequiredTitles,
      message: selectionRequiredMessage
    }),
    [hasSelection, selectionRequiredMessage, selectionRequiredTitles]
  );
  const loadingContextValue = useMemo<ActionBarLoadingContextValue>(() => ({ beginLoading }), [beginLoading]);
  const isActionBarLoading = loading || authLoading || actionLoadingCount > 0;

  return (
    <ActionBarSelectionContext.Provider value={selectionContextValue}>
      <ActionBarLoadingContext.Provider value={loadingContextValue}>
        <div
          className={cn(
            className,
            'flex w-full min-w-0 max-w-full shrink-0 flex-col items-stretch justify-between gap-2 bg-white px-2 py-1 md:flex-row md:items-center md:gap-3',
            'min-h-[var(--erp-action-bar-min-height)]',
            '[&_h1]:truncate [&_h1]:text-[length:var(--erp-action-bar-title-size)] [&_h1]:font-bold [&_h1]:leading-7'
          )}
        >
          <div className='flex min-w-0 flex-1 items-center'>{titleComponent || <h1>{title}</h1>}</div>
          <div className='flex w-full max-w-full shrink-0 items-center justify-end overflow-visible md:w-auto md:min-w-fit'>
            <div
              className={cn('flex w-full shrink-0 flex-wrap items-start justify-end gap-1.5 md:w-auto md:flex-nowrap')}
            >
              {isActionBarLoading && <LoadingSpinner size={30} className='shrink-0 self-center border-4 border-main' />}
              {children}
            </div>
          </div>
        </div>
      </ActionBarLoadingContext.Provider>
    </ActionBarSelectionContext.Provider>
  );
}

export default ActionBarV2;
